import { useId, useState, type InputHTMLAttributes } from 'react'
import { cn } from '../cn.js'

export interface PasswordRule {
  label: string
  test: (value: string) => boolean
}

export interface PasswordFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'type'> {
  label?: string
  error?: string | undefined
  /** Shown BEFORE typing, not as a post-submit error (PLAN.md §11.5). */
  rules?: readonly PasswordRule[]
}

export const DEFAULT_PASSWORD_RULES: readonly PasswordRule[] = [
  { label: 'At least 8 characters', test: (v) => v.length >= 8 },
  { label: 'A lowercase letter', test: (v) => /[a-z]/.test(v) },
  { label: 'An uppercase letter', test: (v) => /[A-Z]/.test(v) },
  { label: 'A number', test: (v) => /\d/.test(v) },
]

export function PasswordField({
  label = 'Password',
  error,
  rules = DEFAULT_PASSWORD_RULES,
  value = '',
  className,
  required,
  ...rest
}: PasswordFieldProps) {
  const id = useId()
  const errorId = `${id}-error`
  const rulesId = `${id}-rules`
  const [revealed, setRevealed] = useState(false)
  const v = String(value)

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
        {required && <span className="sr-only"> (required)</span>}
      </label>

      <div className="relative">
        <input
          id={id}
          type={revealed ? 'text' : 'password'}
          value={value}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={cn(rules.length > 0 && rulesId, error && errorId) || undefined}
          className={cn(
            'min-h-[44px] w-full rounded-lg border bg-white py-2 pl-3 pr-12 text-ink',
            error ? 'border-danger' : 'border-line-strong',
            className,
          )}
          {...rest}
        />
        <button
          type="button"
          onClick={() => setRevealed((r) => !r)}
          // The state must be in the accessible name — an icon alone tells a
          // screen-reader user nothing about whether the password is visible.
          aria-label={revealed ? 'Hide password' : 'Show password'}
          aria-pressed={revealed}
          className="absolute inset-y-0 right-0 flex min-h-[44px] w-12 items-center justify-center rounded-r-lg text-muted"
        >
          <span aria-hidden="true">{revealed ? '🙈' : '👁'}</span>
        </button>
      </div>

      {rules.length > 0 && (
        <ul id={rulesId} className="mt-1 flex flex-col gap-0.5 text-sm">
          {rules.map((rule) => {
            const ok = rule.test(v)
            return (
              <li
                key={rule.label}
                className={cn('flex items-center gap-1.5', ok ? 'text-success' : 'text-muted')}
              >
                {/* Icon carries the state, not just the colour. */}
                <span aria-hidden="true">{ok ? '✓' : '○'}</span>
                <span>{rule.label}</span>
                <span className="sr-only">{ok ? ' — met' : ' — not yet met'}</span>
              </li>
            )
          })}
        </ul>
      )}

      {error && (
        <p id={errorId} role="alert" className="flex items-start gap-1.5 text-sm text-danger">
          <span aria-hidden="true">⚠</span>
          <span>{error}</span>
        </p>
      )}
    </div>
  )
}
